/**
 * SuggestionDiff — Shows a single section suggestion with original vs improved text.
 */

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui';
import type { SectionSuggestion } from '@/types/lyricOrchestrator.types';

interface SuggestionDiffProps {
  suggestion: SectionSuggestion;
}

const changeTypeConfig = {
  keep: { variant: 'success' as const, label: 'Keep' },
  tweak: { variant: 'warning' as const, label: 'Tweak' },
  rewrite: { variant: 'danger' as const, label: 'Rewrite' },
};

export const SuggestionDiff: React.FC<SuggestionDiffProps> = ({ suggestion }) => {
  const [isOpen, setIsOpen] = useState(suggestion.changeType !== 'keep');
  const config = changeTypeConfig[suggestion.changeType];

  return (
    <div className="border border-slate-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold text-slate-300">{suggestion.section}</span>
          <Badge variant={config.variant} size="xs">{config.label}</Badge>
        </div>
        <ChevronDown
          size={12}
          className={`text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="px-3 pb-3 space-y-2 animate-in slide-in-from-top-2 duration-200">
          {suggestion.changeType !== 'keep' && (
            <>
              <div>
                <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Original</span>
                <pre className="text-[11px] text-slate-500 line-through whitespace-pre-wrap font-sans leading-relaxed mt-0.5">
                  {suggestion.originalText}
                </pre>
              </div>
              <div>
                <span className="text-[9px] font-black text-green-500 uppercase tracking-widest">Improved</span>
                <pre className="text-[11px] text-green-300 whitespace-pre-wrap font-sans leading-relaxed mt-0.5 bg-green-950/20 rounded p-2">
                  {suggestion.improvedText}
                </pre>
              </div>
            </>
          )}
          {suggestion.changeType === 'keep' && (
            <p className="text-[11px] text-slate-500 italic">Section is strong — no changes needed.</p>
          )}
          {suggestion.rationale && (
            <p className="text-[10px] text-slate-400 italic border-l-2 border-slate-700 pl-2">
              {suggestion.rationale}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
